import React, { useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, Loader2, AlertCircle, CheckCircle2, X, Building2 } from 'lucide-react';

/**
 * Reusable SIREN/SIRET lookup widget.
 * Calls GET /api/siren-lookup/{query} and lets the parent receive the mapped
 * result via `onSelect(company)` so it can auto-fill any form.
 *
 * Props:
 *   - onSelect(company): called when the user picks a result
 *   - compact: tighter styling (for embedding in modals)
 *   - placeholder: input placeholder
 */
export default function SirenLookup({ onSelect, compact = false, placeholder }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const search = async () => {
    if (!query.trim()) return;
    setError('');
    setResults(null);
    setBusy(true);
    try {
      const r = await api.get(`/siren-lookup/${encodeURIComponent(query.trim())}`);
      setResults(r.data.results || []);
      if ((r.data.results || []).length === 0) {
        setError('Aucune société trouvée. Vous pouvez saisir les informations manuellement ci-dessous.');
      }
    } catch (e) {
      setError(e?.response?.data?.detail || 'API SIRENE indisponible. Saisissez manuellement.');
    }
    setBusy(false);
  };

  const handleKey = (e) => { if (e.key === 'Enter') { e.preventDefault(); search(); } };

  return (
    <div className={`bg-blue-50/50 border-2 border-dashed border-blue-200 rounded-lg ${compact ? 'p-3' : 'p-4'} space-y-2`} data-testid="siren-lookup">
      <div className="flex items-center gap-2 text-blue-900">
        <Building2 className="w-4 h-4" />
        <p className={`font-semibold ${compact ? 'text-sm' : ''}`}>Rechercher une société (SIREN, SIRET ou nom)</p>
      </div>
      <p className="text-xs text-slate-500">Récupération automatique des coordonnées légales depuis l'INSEE.</p>
      <div className="flex gap-2">
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKey}
          placeholder={placeholder || 'Ex: 818305864, 81830586400030 ou Reno Resolve'}
          data-testid="siren-input"
        />
        <Button onClick={search} disabled={busy || !query.trim()} className="bg-blue-600 hover:bg-blue-700" data-testid="siren-search-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
        </Button>
      </div>

      {error && (
        <div className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
          <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {results && results.length > 0 && (
        <div className="space-y-1 mt-2">
          <p className="text-xs font-semibold text-slate-700">{results.length} résultat(s) trouvé(s) :</p>
          {results.map((r, i) => (
            <button
              key={r.siren + i}
              type="button"
              onClick={() => { onSelect(r); setResults(null); setQuery(''); }}
              className="w-full text-left p-2.5 bg-white border border-slate-200 hover:border-blue-400 hover:bg-blue-50 rounded-lg transition-all flex items-start gap-2"
              data-testid={`siren-result-${i}`}
            >
              <CheckCircle2 className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm text-slate-900">{r.nom}</p>
                <p className="text-xs text-slate-500">
                  SIREN <span className="font-mono">{r.siren}</span>
                  {r.forme_juridique_libelle && ` · ${r.forme_juridique_libelle}`}
                </p>
                <p className="text-xs text-slate-500 mt-0.5">{r.adresse} {r.code_postal} {r.ville}</p>
                {r.representant_nom && (
                  <p className="text-xs text-slate-400 italic">{r.representant_qualite || 'Représentant'} : {r.representant_nom}</p>
                )}
                {r.etat_administratif && r.etat_administratif !== 'A' && (
                  <p className="text-xs text-red-600 font-semibold mt-0.5">⚠️ Société fermée</p>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
